import { lazy } from "react";

/**
 * Drop-in replacement for React.lazy that survives a deploy.
 *
 * The problem it solves: index.html names the hashed bundles of the deploy it
 * came from. A tab that has been open across a deploy - or one holding a
 * cached index.html, or a cached error response for a chunk URL - asks for a
 * chunk the server will not give it. The request comes back 403/404, the lazy
 * import rejects, and with no error boundary above it the whole app unmounts
 * to a blank page. Clearing the browser cache fixes it, which is not something
 * we can ask people to do.
 *
 * Recovery is just to reload: that fetches a fresh index.html (served
 * no-cache) which names the chunks that actually exist now.
 *
 * The sessionStorage flag is what keeps this from becoming a reload loop. If a
 * chunk still fails immediately after a reload, the failure is real - a broken
 * deploy, no network - so the error is rethrown and surfaces normally instead
 * of the page reloading forever. The flag is cleared on the next success, so a
 * later deploy gets its own single retry.
 */
const reloadKey = "chunk-reload-attempted";

const lazyWithRetry = (factory) =>
  lazy(async () => {
    try {
      const component = await factory();
      // Loaded fine - let a future deploy have its own retry.
      try {
        window.sessionStorage.removeItem(reloadKey);
      } catch {
        // Private mode / blocked storage: nothing to clean up.
      }
      return component;
    } catch (error) {
      let alreadyTried = false;
      try {
        alreadyTried = window.sessionStorage.getItem(reloadKey) === "true";
        if (!alreadyTried) window.sessionStorage.setItem(reloadKey, "true");
      } catch {
        // If sessionStorage is unavailable we cannot tell a first failure from
        // a repeat one. Rethrow rather than risk an endless reload loop.
        throw error;
      }

      if (alreadyTried) throw error;

      window.location.reload();
      // The reload is on its way; never resolve, so React keeps showing the
      // suspense fallback instead of flashing an error first.
      return new Promise(() => { });
    }
  });

export default lazyWithRetry;
